/**
 * CommandPalette — Cmd/Ctrl+K busca global no GridProfile.
 * GridProfile / NeuroSynx Ltda
 *
 * Funcionalidades:
 *   - Atalho Cmd+K (macOS) / Ctrl+K (Windows/Linux) abre o palette
 *   - Navegação rápida para todas as telas principais (rotas)
 *   - Busca por funcionários
 *   - Busca por competências DP
 *   - Busca por eventos eSocial (por tipo/ID)
 *   - Quando não há resultado, oferece "Pergunte à Maya sobre [busca]" integrado ao function calling
 */
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator, CommandShortcut,
} from './ui/command';
import {
  Search, Sparkles, FileText, Building2, Users, Calendar,
  ShieldCheck, Receipt, HeartPulse, GraduationCap, BarChart3,
  Settings, Briefcase, MapPin, Bell, ScrollText, Mail,
  Calculator, Clock, FileCode, UserX,
} from 'lucide-react';
import { useMaya } from '../contexts/MayaContext';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// =============================================================================
// Mapa estático de rotas/telas (carregado on-the-fly, sem chamada à API)
// =============================================================================
const TELAS = [
  // Dashboard / DP / Folha
  { label: 'Dashboard',                path: '/dashboard',                  icon: BarChart3,    keywords: 'inicio home painel kpis' },
  { label: 'DP/Folha — Visão Geral',   path: '/dp-folha',                   icon: Briefcase,    keywords: 'departamento pessoal folha' },
  { label: 'Folha de Pagamento',       path: '/dp-folha/folha',             icon: Calculator,   keywords: 'holerite competencia rubricas calcular' },
  { label: '13º · Férias · Rescisões', path: '/dp-folha/rescisoes',         icon: ScrollText,   keywords: '13 decimo terceiro ferias rescisao demissao' },
  { label: 'Afastamentos',             path: '/dp-folha/afastamentos',       icon: UserX,        keywords: 'afastamento licenca saude inss retorno' },
  { label: 'Remessa CNAB 240',         path: '/dp-folha/cnab240',           icon: FileText,     keywords: 'cnab 240 banco remessa pagamento febraban' },
  { label: 'eSocial — Eventos',        path: '/dp-folha/esocial',           icon: FileCode,     keywords: 'esocial s-1200 s-1210 s-2299 s-1299 s-2210 xml' },
  { label: 'Obrigações Fiscais',       path: '/dp-folha/obrigacoes',        icon: Receipt,      keywords: 'dctfweb gps darf grrf irrf inss' },
  { label: 'SST Avançado',             path: '/dp-folha/sst-avancado',      icon: HeartPulse,   keywords: 'pcmso pgr cat saude seguranca trabalho nr7 nr1' },
  { label: 'Certificados Digitais',    path: '/dp-folha/certificados',      icon: ShieldCheck,  keywords: 'a1 icp-brasil pfx assinar' },
  { label: 'Saúde Ocupacional',        path: '/dp-folha/saude',             icon: HeartPulse,   keywords: 'aso epi ocupacional medico' },
  { label: 'Cadastro Trabalhista',     path: '/dp-folha/cadastro-trab',     icon: Users,        keywords: 'cadastro trabalhista' },
  { label: 'Disciplinar',              path: '/dp-folha/disciplinar',       icon: ScrollText,   keywords: 'advertencia suspensao disciplinar' },
  { label: 'Legislação',               path: '/dp-folha/legislacao',        icon: ScrollText,   keywords: 'legislacao inss irrf fgts salario minimo familia vt' },
  { label: 'Sindicatos',               path: '/dp-folha/sindicatos',        icon: Building2,    keywords: 'sindicato' },
  { label: 'Convenções Coletivas',     path: '/dp-folha/convencoes',        icon: ScrollText,   keywords: 'cct convencao coletiva acordo' },
  { label: 'Centros de Custo',         path: '/dp-folha/centros-custo',     icon: Briefcase,    keywords: 'centro custo' },
  // Time Tracking
  { label: 'Ponto Eletrônico',         path: '/timetracking',               icon: Clock,        keywords: 'ponto eletronico bater entrada saida' },
  { label: 'Admin Ponto',              path: '/timetracking-admin',         icon: Clock,        keywords: 'admin ponto espelho ajustes' },
  { label: 'Banco de Horas',           path: '/banco-horas',                icon: Clock,        keywords: 'banco horas saldo' },
  // Outros
  { label: 'Registro de Viagens',      path: '/registro-viagem',            icon: MapPin,       keywords: 'viagem gps localizacao' },
  { label: 'Notificações',             path: '/notifications',              icon: Bell,         keywords: 'notificacao aviso' },
  { label: 'Grid Learning (LMS)',      path: '/lms',                        icon: GraduationCap, keywords: 'curso treinamento certificado lms ead' },
  { label: 'Cargos e Salários',        path: '/configuracoes/cargos-salarios', icon: Briefcase, keywords: 'cargos salarios remuneracao' },
  { label: 'Email — Configurações',    path: '/configuracoes/email',        icon: Mail,         keywords: 'email smtp config' },
  { label: 'Configurações',            path: '/configuracoes',              icon: Settings,     keywords: 'config sistema' },
];

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [funcionarios, setFuncionarios] = useState([]);
  const [competencias, setCompetencias] = useState([]);
  const [eventosEsocial, setEventosEsocial] = useState([]);
  const [loadedData, setLoadedData] = useState(false);
  const navigate = useNavigate();
  const { askMaya } = useMaya();
  const isAuthed = !!localStorage.getItem('token');

  // Atalho Cmd/Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Carregar dados dinâmicos quando o palette abre (uma vez por sessão)
  useEffect(() => {
    if (open && !loadedData && isAuthed) {
      Promise.allSettled([
        axios.get(`${API}/api/employees`, auth()).then(r => r.data || []),
        axios.get(`${API}/api/calculos-dp/competencias`, auth()).then(r => r.data || []),
        axios.get(`${API}/api/esocial/eventos`, auth()).then(r => (r.data || []).slice(0, 50)),
      ]).then(([f, c, e]) => {
        setFuncionarios(f.status === 'fulfilled' ? f.value : []);
        setCompetencias(c.status === 'fulfilled' ? c.value : []);
        setEventosEsocial(e.status === 'fulfilled' ? e.value : []);
        setLoadedData(true);
      });
    }
  }, [open, loadedData, isAuthed]);

  // Limpar query ao fechar
  useEffect(() => { if (!open) setQuery(''); }, [open]);

  const handleSelect = useCallback((path) => {
    setOpen(false);
    navigate(path);
  }, [navigate]);

  const handleAskMaya = useCallback(() => {
    setOpen(false);
    askMaya(`Me ajude a encontrar ou entender: ${query}`);
  }, [askMaya, query]);

  // Filtro client-side simples
  const q = query.trim().toLowerCase();
  const telasFiltradas = useMemo(() => {
    if (!q) return TELAS.slice(0, 12);
    return TELAS.filter(t =>
      t.label.toLowerCase().includes(q) ||
      (t.keywords || '').toLowerCase().includes(q)
    ).slice(0, 12);
  }, [q]);

  const funcsFiltrados = useMemo(() => {
    if (!q || !funcionarios.length) return [];
    return funcionarios.filter(e =>
      (e.nome || '').toLowerCase().includes(q) ||
      (e.cpf || '').includes(q) ||
      (e.email || '').toLowerCase().includes(q)
    ).slice(0, 8);
  }, [q, funcionarios]);

  const compsFiltradas = useMemo(() => {
    if (!q || !competencias.length) return [];
    return competencias.filter(c => {
      const label = `${MESES[c.mes - 1]} ${c.ano} ${c.tipo} ${c.status}`.toLowerCase();
      return label.includes(q);
    }).slice(0, 6);
  }, [q, competencias]);

  const eventosFiltrados = useMemo(() => {
    if (!q || !eventosEsocial.length) return [];
    return eventosEsocial.filter(e =>
      (e.tipo || '').toLowerCase().includes(q) ||
      (e.id_esocial || '').toLowerCase().includes(q) ||
      (e.employee_nome || '').toLowerCase().includes(q) ||
      (e.status || '').toLowerCase().includes(q)
    ).slice(0, 8);
  }, [q, eventosEsocial]);

  const semResultados = q && !telasFiltradas.length && !funcsFiltrados.length && !compsFiltradas.length && !eventosFiltrados.length;

  if (!isAuthed) return null;

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Buscar telas, funcionários, competências, eventos eSocial..."
          value={query}
          onValueChange={setQuery}
          data-testid="cmdk-input"
        />
        <CommandList data-testid="cmdk-list">
          <CommandEmpty>
            {semResultados ? (
              <div className="py-4 text-center">
                <p className="text-sm text-slate-500 mb-3">Nada encontrado para "<b>{query}</b>"</p>
                <button
                  onClick={handleAskMaya}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white text-sm rounded-full px-4 py-2 hover:opacity-90"
                  data-testid="cmdk-ask-maya"
                >
                  <Sparkles className="h-4 w-4" /> Pergunte à Maya sobre "{query}"
                </button>
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-slate-500">Digite para buscar...</p>
            )}
          </CommandEmpty>

          {telasFiltradas.length > 0 && (
            <CommandGroup heading="Telas">
              {telasFiltradas.map((t) => {
                const Icon = t.icon;
                return (
                  <CommandItem
                    key={t.path}
                    value={`tela-${t.label}-${t.keywords}`}
                    onSelect={() => handleSelect(t.path)}
                    data-testid={`cmdk-tela-${t.path.replace(/\//g, '-')}`}
                  >
                    <Icon className="h-4 w-4 mr-2 text-slate-500" />
                    <span className="flex-1">{t.label}</span>
                    <span className="text-[10px] text-slate-400 font-mono">{t.path}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          {funcsFiltrados.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Funcionários">
                {funcsFiltrados.map((e) => (
                  <CommandItem
                    key={e.id}
                    value={`func-${e.nome}-${e.cpf}`}
                    onSelect={() => handleSelect(`/employees/${e.id}`)}
                    data-testid={`cmdk-func-${e.id}`}
                  >
                    <Users className="h-4 w-4 mr-2 text-emerald-600" />
                    <div className="flex-1">
                      <p className="text-sm">{e.nome}</p>
                      <p className="text-[10px] text-slate-400">{e.cargo || '—'} · CPF {e.cpf || '—'}</p>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {compsFiltradas.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Competências DP">
                {compsFiltradas.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={`comp-${MESES[c.mes - 1]}-${c.ano}-${c.tipo}-${c.status}`}
                    onSelect={() => handleSelect('/dp-folha/folha')}
                    data-testid={`cmdk-comp-${c.id}`}
                  >
                    <Calendar className="h-4 w-4 mr-2 text-indigo-600" />
                    <span className="flex-1">{MESES[c.mes - 1]}/{c.ano} — {c.tipo}</span>
                    <span className="text-[10px] uppercase text-slate-400">{c.status}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {eventosFiltrados.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Eventos eSocial">
                {eventosFiltrados.map((ev) => (
                  <CommandItem
                    key={ev.id}
                    value={`ev-${ev.tipo}-${ev.id_esocial}-${ev.employee_nome}`}
                    onSelect={() => handleSelect('/dp-folha/esocial')}
                    data-testid={`cmdk-evento-${ev.id}`}
                  >
                    <FileCode className="h-4 w-4 mr-2 text-purple-600" />
                    <div className="flex-1">
                      <p className="text-sm">{ev.tipo} {ev.employee_nome ? `· ${ev.employee_nome}` : ''}</p>
                      <p className="text-[10px] text-slate-400 font-mono">{ev.id_esocial}</p>
                    </div>
                    <span className="text-[10px] uppercase text-slate-400">{ev.status}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Atalho permanente para Maya */}
          {q && !semResultados && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Maya">
                <CommandItem onSelect={handleAskMaya} data-testid="cmdk-maya-suggestion">
                  <Sparkles className="h-4 w-4 mr-2 text-indigo-500" />
                  <span className="flex-1">Pergunte à Maya sobre "<b>{query}</b>"</span>
                </CommandItem>
              </CommandGroup>
            </>
          )}
        </CommandList>

        <div className="border-t px-3 py-2 text-[10px] text-slate-400 flex items-center justify-between">
          <span>Navegue com ↑↓, Enter para selecionar</span>
          <span className="flex items-center gap-2">
            <kbd className="px-1.5 py-0.5 border rounded bg-slate-50 font-mono">⌘K</kbd>
            <span>para abrir/fechar</span>
          </span>
        </div>
      </CommandDialog>
    </>
  );
};

export default CommandPalette;
